import {
  enableProdMode,
  signal,
  importProvidersFrom,
  ApplicationConfig,
  APP_ID,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { environment } from './environments/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { parseAgrEnvironment } from '@next/core-lib/environment';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { routes } from './app/app.routes';
import { coreRoutes, provideAgrCore } from '@next/core-lib/app';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { CoreEffects } from '@next/core-lib';
import { I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { DialogService } from '@next/core-lib/dialog';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { provideAgrResponsiveBrowser } from '@next/core-lib/responsive-browser';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { AgrPWAModule } from '@next/core-lib/pwa';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { AgrBrandingModule } from '@next/core-lib/branding';
import { AppComponent } from './app/app.component';
import { Environment } from './environments/environment.model';
import { AssociatedChainPartnerStore } from './app/associated-chain-partner/store/associated-chain-partner.store';
import { chainPartyInterceptor } from './app/associated-chain-partner/interceptors/chain-party.interceptor';

if (environment.PRODUCTION) {
  enableProdMode();
}

const loadedConfig = signal<Environment>(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const response = await globalThis.fetch('/assets/config.json');
    if (response.ok) {
      const config = (await response.json()) as Record<string, string | undefined>;
      loadedConfig.set({
        ...environment,
        ...parseAgrEnvironment(config),
      });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([...routes, ...coreRoutes], withEnabledBlockingInitialNavigation()),
    provideHttpClient(withInterceptors([chainPartyInterceptor]), withInterceptorsFromDi()),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    provideEffects([CoreEffects, I18nEffects, I18nBrowserEffects, NavigationEffects, SiteEffects]),
    provideAgrRouterStore(),
    provideAgrSite(),
    provideAgrCore(),
    provideAgrBrowser(),
    provideAgrResponsiveBrowser(),
    DialogService,
    provideAgrElasticApi(),
    provideAgrI18nShared(),
    provideAgrSettings(),
    provideAgrNavigation(),
    provideAgrWrapperSiteSidenav(),
    provideEnvironmentInitializer(() => inject(AssociatedChainPartnerStore)),
    importProvidersFrom(AgrPWAModule, AgrBrandingModule),
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
  ],
};

setupRuntimeConfig.then(() => {
  bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
});
